import { useCallback } from 'react';
import { Editor, Range as SlateRange, Text } from 'slate';
import { ReactEditor } from 'slate-react';
import { escapeRegExp } from 'lodash';

import { COLORS } from 'src/data/ui';
import { getWordBoundaryRegExp } from 'src/lib/string';
import { HIGHLIGHT_MARK } from 'src/data/markedText';

const matchingIndexes = (text, word) => {
  const escapedKeyword = escapeRegExp(word);
  return [...text.matchAll(getWordBoundaryRegExp(escapedKeyword.toLowerCase()))].map((a) =>
    a.index !== 0 ? a.index + 1 : a.index
  );
};

/*
note: links decoration was removed in this commit: https://github.com/KeyweeLabs/post-malone/pull/9214/commits/ad7dc20306bafcd00c0d805e05a4a48e45c7ce45
 */

export const useDecorateBlogSection = ({
  editor,
  highlightedWords,
  decoratedWords,
  highlightedRange,
  decorateUnfocusedSelection,
}) => {
  return useCallback(
    ([node, path]) => {
      if (!Text.isText(node)) {
        return [];
      }

      const ranges = [];
      highlightedWords?.forEach((word) => {
        const indexes = matchingIndexes(node.text, word);

        const newRanges = indexes.map((index) => {
          const endIndex = index + word.length;
          return {
            anchor: { path, offset: index },
            focus: { path, offset: endIndex },
            highlight: true,
          };
        });
        ranges.push(...newRanges);
      });

      decoratedWords?.forEach((word) => {
        const indexes = matchingIndexes(node.text, word);

        const newRanges = indexes.map((index) => {
          const endIndex = index + word.length;
          return {
            anchor: { path, offset: index },
            focus: { path, offset: endIndex },
            decoration: word.decoration || HIGHLIGHT_MARK.warning,
          };
        });
        ranges.push(...newRanges);
      });

      if (highlightedRange) {
        const nodeRange = Editor.range(editor, path);
        const intersection = SlateRange.intersection(highlightedRange, nodeRange);
        if (intersection) {
          ranges.push({
            anchor: intersection.anchor,
            focus: intersection.focus,
            backgroundColor: COLORS.primary.light,
          });
        }
      }

      if (
        decorateUnfocusedSelection &&
        !ReactEditor.isFocused(editor) &&
        editor.getPreviousSelection
      ) {
        const selectionRange = editor.getPreviousSelection();
        if (selectionRange) {
          const nodeRange = Editor.range(editor, path);
          const intersection = SlateRange.intersection(selectionRange, nodeRange);
          if (intersection) {
            ranges.push({
              anchor: intersection.anchor,
              focus: intersection.focus,
              selection: true,
            });
          }
        }
      }

      return ranges;
    },
    [decoratedWords, highlightedWords, highlightedRange, editor, decorateUnfocusedSelection]
  );
};
